import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { submitChangeRequest, SubmitChangeRequestOptions } from '@/services/buyer-dashboard';
import { ValidationForm, ValidatingTextArea, ValidatingInput, ValidationSummary } from '@/components/forms/validation';
import { Button } from '@/components/Buttons';
import { Dispatch } from 'redux';
import { IState } from '@/state/IState';
import theme from './BuyerChangeRequestCreate.module.scss';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import ChangeRequestAttachmentList from '@/seller/components/change-requests/ChangeRequestAttachmentList';
import { ChangeRequestAttachment } from '@/types';
import { Modal, ModalBody, ModalHeader } from '@ygtechnologies/bygr-frontend-component-library';
import { connect } from 'react-redux';
import Banner from '@/components/Banner';

const mapDispatchToProps = (dispatch: Dispatch) => ({});
const mapStateToProps = (state: IState) => ({
    residentialUnitId: state.buyer.permissions!.residentialUnitId,
});

type Props = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> & {
        isOpen: boolean;
        onClose(): void;
        onSubmit?: (id: number) => void;
    };

const BuyerChangeRequestCreate: React.FC<Props> = ({ residentialUnitId, onSubmit, isOpen, onClose }) => {
    const [saving, setSaving] = useState(false);
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [attachments, setAttachments] = useState<ChangeRequestAttachment[]>([]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const request: SubmitChangeRequestOptions = {
            subject,
            description,
            attachments,
        };

        setSaving(true);

        try {
            const changeRequestId = await submitChangeRequest(residentialUnitId, request);
            triggerHandler(onClose);
            triggerHandler(onSubmit, changeRequestId);
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setSubject('');
        setDescription('');
        setAttachments([]);
    };

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={onClose}
            onOpen={reset}
            closeOnEscape={false}>
            <ModalHeader>
                <h6>
                    <Trans>Create a new change request</Trans>
                </h6>
            </ModalHeader>
            <ModalBody>
                <ValidationForm onSubmit={handleSubmit} className={theme.form}>
                    <div className="form-group">
                        <label>
                            <Trans>Title</Trans>
                        </label>
                        <ValidatingInput
                            fieldName={t`Title`}
                            required
                            className="form-control"
                            placeholder={t`What is the change about? Also, a text that will help you find again this change request easily.`}
                            value={subject}
                            name="subject"
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <Trans>Description</Trans>
                        </label>
                        <ValidatingTextArea
                            fieldName={t`Description`}
                            required
                            className="form-control"
                            rows={6}
                            placeholder={t`Describe in your own words what you would like us to do with your home. Be as descriptive as possible.`}
                            value={description}
                            name="description"
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>
                    <Banner type="info">
                        <Trans>
                            Attachments that can help the contractors understand your request may save time for all of us,
                            and will help us provide you quickly with an offer for what you actually want.
                        </Trans>
                    </Banner>
                    <div className="form-group">
                        <ChangeRequestAttachmentList
                            useUploadWizard
                            value={attachments}
                            onChange={(newValue) => setAttachments(newValue)}
                            allowPaste
                        />
                    </div>
                    <ValidationSummary />
                    <div className={theme.buttons}>
                        <Button onClick={onClose} color="tertiary">
                            <Trans>Discard</Trans>
                        </Button>
                        <Button type="submit" color="primary" loading={saving}>
                            <Trans>Ask for quote</Trans>
                        </Button>
                    </div>
                </ValidationForm>
            </ModalBody>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(BuyerChangeRequestCreate);
